import { useState, useEffect, useCallback } from 'react';
import config from '../config';

const API_ENDPOINT = config.API_ENDPOINT;
const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to fetch and manage ski sale configuration
 * Provides real-time status updates and caching
 */
export const useSkiSaleConfig = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      console.log('Fetching ski sale config from:', `${API_ENDPOINT}/config/status`);
      const response = await fetch(`${API_ENDPOINT}/config/status`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch config: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Received ski sale config:', data);
      setStatus(data.consignmentStatus);
      setLastUpdated(new Date(data.lastUpdated));
      setError(null);
    } catch (err) {
      console.error('Error fetching ski sale config:', err);
      setError(err.message);
      // Fallback to 'open' if we can't reach the API and have no previous status
      if (!status) {
        setStatus('open');
      }
    } finally {
      setLoading(false);
    }
  }, []);  // Remove status dependency to prevent stale closures

  // Initial fetch
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Set up periodic refresh
  useEffect(() => {
    const interval = setInterval(fetchConfig, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchConfig]);

  // Manual refresh function
  const refresh = useCallback(() => {
    setLoading(true);
    fetchConfig();
  }, [fetchConfig]);

  // Helper functions
  const isOpen = status === 'open';
  const isClosed = status === 'closed';
  const isMaintenance = status === 'maintenance';

  const getStatusMessage = () => {
    switch (status) {
      case 'open':
        return 'The ski sale is currently open for consignments.';
      case 'closed':
        return 'The ski sale is currently closed. Please check back later.';
      case 'maintenance':
        return 'The ski sale system is temporarily under maintenance. Please try again later.';
      default:
        return 'Checking ski sale status...';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'open':
        return 'success';
      case 'closed':
        return 'warning';
      case 'maintenance':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  return {
    status,
    loading,
    error,
    lastUpdated,
    isOpen,
    isClosed,
    isMaintenance,
    getStatusMessage,
    getStatusColor,
    refresh
  };
};

export default useSkiSaleConfig;
